import type { TaxReturn } from '../../types';

/**
 * Whether the UI must offer a way to RECORD a spouse on this return.
 *
 * The bug this exists to prevent (production, 2026-02-05):
 *
 *   The "Add Spouse" button was gated on `filingType !== 'joint'`, while the Spouse
 *   Information block — which holds the spouse contact picker — was gated on
 *   `filingType === 'joint' && spouseName`. A return that was ALREADY joint but had no
 *   spouse recorded fell in the gap between those two conditions: the button was hidden
 *   *because* it was joint, and the picker was hidden *because* there was no spouse yet.
 *
 *   So a joint return with no spouse had NO way to record one, and the only editable name
 *   field on screen was the client name. A staff member typed the spouse's name there. At
 *   the time that field wrote back to Dataverse, so it renamed the taxpayer's shared CRM
 *   contact — "Daniel Martin Alfonso" became "Lisandra Zayas" on a contact referenced by
 *   two other returns and two appointments.
 *
 * The write-back is gone (see TaxReturnFunctions, 2026-07-31). This closes the affordance
 * gap that produced the intent in the first place: the offer depends on whether a spouse is
 * RECORDED, never on filingType.
 */
export function canRecordSpouse(taxReturn: Pick<TaxReturn, 'returnType' | 'spouseName'>): boolean {
  if (taxReturn.returnType !== 'individual') return false;
  return !hasSpouseRecorded(taxReturn);
}

/** A spouse counts as recorded only when there is a non-blank name to show. */
export function hasSpouseRecorded(taxReturn: Pick<TaxReturn, 'spouseName'>): boolean {
  return !!taxReturn.spouseName && taxReturn.spouseName.trim().length > 0;
}

/**
 * The exact defect state: a joint return carrying no spouse. Kept as its own predicate so
 * the regression is named in the code and can be asserted directly.
 */
export function isJointMissingSpouse(
  taxReturn: Pick<TaxReturn, 'returnType' | 'filingType' | 'spouseName'>,
): boolean {
  return (
    taxReturn.returnType === 'individual' &&
    taxReturn.filingType === 'joint' &&
    !hasSpouseRecorded(taxReturn)
  );
}
